/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useSyncExternalStore } from 'react';
import {
  getValidatedSelectedCompanyIdForRequest,
  subscribeValidatedSelectedCompanyIdForRequest,
} from '../config/company-scope.config';
import {
  getDmsFileImageStableUrl,
  isDmsFileRef,
  prefetchDmsImages,
  resolveDmsMediaCallerScopeId,
  type DmsMediaUrlOptions,
} from './dmsMedia';

/**
 * Geriye dönük API. Kalıcı DMS görselleri artık presigned URL/session cache kullanmaz;
 * dolayısıyla logout sırasında temizlenecek storage URL'i yoktur.
 */
export function clearQubeproMediaPreviewCache(): void {
  // Intentionally empty: storage-opaque same-origin URLs contain no session secret.
}

export function prefetchQubeproMediaPreviewUrls(
  storedValues: Array<string | null | undefined>,
  options: DmsMediaUrlOptions & { maxItems?: number; concurrency?: number } = {},
): Promise<void> {
  const maxItems = Math.max(1, options.maxItems ?? 48);
  const concurrency = Math.max(1, Math.min(options.concurrency ?? 4, 8));
  const companyScope =
    resolveDmsMediaCallerScopeId(options.companyScope, options.systemMedia) ||
    resolveDmsMediaCallerScopeId(
      getValidatedSelectedCompanyIdForRequest(),
      options.systemMedia,
    );
  if (!companyScope) return Promise.resolve();
  const queue = Array.from(new Set(storedValues
    .map((value) => value?.trim() ?? '')
    .filter((value) => value && isDmsFileRef(value)))).slice(0, maxItems);

  if (queue.length === 0) return Promise.resolve();
  void concurrency; // Backward-compatible option; browser cache handles parallelism.
  prefetchDmsImages(queue.map((ref) => ({
    ref,
    companyScope,
    systemMedia: options.systemMedia,
  })));
  return Promise.resolve();
}

export function useQubeproMediaPreviewUrl(
  stored: string | null | undefined,
  options: DmsMediaUrlOptions = {},
): {
  displayUrl: string | null;
  resolving: boolean;
} {
  const validatedCompanyScope = useSyncExternalStore(
    subscribeValidatedSelectedCompanyIdForRequest,
    getValidatedSelectedCompanyIdForRequest,
    () => null,
  );
  const companyScope =
    resolveDmsMediaCallerScopeId(options.companyScope, options.systemMedia) ||
    resolveDmsMediaCallerScopeId(validatedCompanyScope, options.systemMedia);
  const isStoredDmsRef = isDmsFileRef(stored);
  const displayUrl = isStoredDmsRef
    ? (companyScope
      ? getDmsFileImageStableUrl(stored, {
        companyScope,
        systemMedia: options.systemMedia,
      })
      : null)
    : getDmsFileImageStableUrl(stored);
  const resolving = Boolean(isStoredDmsRef && !companyScope);

  return { displayUrl, resolving };
}
